import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';

export default function ManageModules() {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [moduleLessons, setModuleLessons] = useState([]);
  const [moduleLessonMap, setModuleLessonMap] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchModulesAndLessons = useCallback(async () => {
    setLoading(true);

    const { data: mods, error: modErr } = await supabase
      .from('modules')
      .select('*')
      .eq('created_by', user.id);

    if (modErr) {
      console.error('Error fetching modules:', modErr);
      return;
    }

    const { data: allLessons, error: lessonErr } = await supabase
      .from('module_lessons')
      .select('id, title, module_id');

    if (lessonErr) {
      console.error('Error fetching module lessons:', lessonErr);
      return;
    }

    const grouped = {};
    for (const lesson of allLessons) {
      if (!lesson.module_id) continue;
      if (!grouped[lesson.module_id]) grouped[lesson.module_id] = [];
      grouped[lesson.module_id].push(lesson);
    }

    setModules(mods);
    setModuleLessons(allLessons);
    setModuleLessonMap(grouped);
    setLoading(false);
  }, [user]);

  const handleTogglePublic = async (moduleId, currentValue) => {
    const { error } = await supabase
      .from('modules')
      .update({ is_public: !currentValue })
      .eq('id', moduleId);

    if (error) {
      alert('Error updating visibility: ' + error.message);
    } else {
      fetchModulesAndLessons();
    }
  };

  const handleAssignLesson = async (lessonId, moduleId) => {
    const { error } = await supabase
      .from('module_lessons')
      .update({ module_id: moduleId })
      .eq('id', lessonId);

    if (error) {
      alert('Error assigning lesson: ' + error.message);
    } else {
      fetchModulesAndLessons();
    }
  };

  const handleUnassignLesson = async (lessonId) => {
    const { error } = await supabase
      .from('module_lessons')
      .update({ module_id: null })
      .eq('id', lessonId);

    if (error) {
      alert('Error unassigning lesson: ' + error.message);
    } else {
      fetchModulesAndLessons();
    }
  };

  useEffect(() => {
    if (user?.isAdmin) fetchModulesAndLessons();
  }, [user, fetchModulesAndLessons]);

  if (!user?.isAdmin) return <p className="text-red-600">Access denied.</p>;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-gray-800">Manage Modules</h2>
        <button
          onClick={() => navigate('/admin/create-module')}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
        >
          + Create Module
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : modules.length === 0 ? (
        <p>No modules yet.</p>
      ) : (
        modules.map((mod) => {
          const assignedLessons = moduleLessonMap[mod.id] || [];
          const unassignedLessons = moduleLessons.filter(
            (l) => !l.module_id || l.module_id !== mod.id
          );

          return (
            <div
              key={mod.id}
              className="bg-white border border-gray-300 rounded p-6 shadow space-y-4"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {mod.name}
                  </h3>
                  <p className="text-sm text-gray-600">{mod.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/module-settings/${mod.id}`)}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Manage Settings
                  </button>
                  <button
                    onClick={() =>
                      handleTogglePublic(mod.id, mod.is_public)
                    }
                    className={`text-xs px-2 py-1 rounded ${
                      mod.is_public
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-300 text-gray-800'
                    }`}
                  >
                    {mod.is_public ? 'Make Private' : 'Make Public'}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-1">Lessons in Module:</h4>
                {assignedLessons.length === 0 ? (
                  <p className="text-gray-500 mb-2">No lessons assigned.</p>
                ) : (
                  <ul className="mb-2">
                    {assignedLessons.map((l) => (
                      <li
                        key={l.id}
                        className="flex justify-between items-center mb-1"
                      >
                        <span>{l.title}</span>
                        <button
                          onClick={() => handleUnassignLesson(l.id)}
                          className="text-red-500 text-sm hover:underline"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-1">Add Lesson:</h4>
                <select
                  className="p-2 border rounded"
                  onChange={(e) => {
                    const lessonId = e.target.value;
                    if (lessonId) {
                      handleAssignLesson(lessonId, mod.id);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">-- Select a lesson --</option>
                  {unassignedLessons.map((lesson) => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
